import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import { fetchDictOptions } from '@/redux/components/dictOptionsSlice';
import { fetchYingshiDeviceList } from '@/redux/yingshi/deviceListSlice';
import { addMonitoringAlarmProcessed } from '@/redux/sse/sseSlice';
import { markYingshiDeviceFalsePositive } from '@/service/yingshi';
import { babyMonitorDevices, readBabyMonitorView } from './BabyMonitor';

const BED_TONE = {
  normal: { color: '#00d4aa', label: '正常' },
  room: { color: '#00e5ff', label: '回房' },
  absent: { color: '#fbbf24', label: '无婴' },
  alert: { color: '#ff6b6b', label: '异常' },
  empty: { color: '#475569', label: '离线' }
};

const TONE_ORDER = { alert: 0, absent: 1, room: 2, normal: 3, empty: 4 };

function toneOf(view) {
  if (!view.online) return 'empty';
  if (view.dest === '送回待确认') return 'room';
  if (view.warning && view.hasBaby === false) return 'absent';
  if (view.warning) return 'alert';
  return 'normal';
}

function bedSortNum(bedNo, channelNo) {
  const raw = bedNo == null ? '' : String(bedNo).trim();
  const n = Number(raw);
  if (raw !== '' && Number.isFinite(n)) return n;
  const ch = Number(channelNo);
  return Number.isFinite(ch) ? ch : 9999;
}

function apiErrorText(error, fallback) {
  if (error && typeof error === 'object' && (error.msg || error.message)) {
    return String(error.msg || error.message);
  }
  return fallback;
}

function MapView({ storeId }) {
  const dispatch = useDispatch();
  const yingshiDeviceList = useSelector((state) => state.yingshi.deviceList);
  const sse = useSelector((state) => state.sse);
  const dictOptions = useSelector((state) => state.components.dictOptions);
  const [openKey, setOpenKey] = useState('');
  const [wrongPendingKey, setWrongPendingKey] = useState('');

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';

  useEffect(() => {
    dispatch(fetchDictOptions({ typeCode: 'nursing_type' }));
  }, [dispatch]);

  const nursingLabelByValue = useMemo(() => {
    const list = Array.isArray(dictOptions?.nursing_type?.data)
      ? dictOptions.nursing_type.data
      : [];
    const map = new Map();
    for (let i = 0; i < list.length; i += 1) {
      const item = list[i];
      if (!item || item.value == null || item.value === '') continue;
      const raw = item.label != null ? item.label : (item.desc != null ? item.desc : item.value);
      map.set(String(item.value), String(raw == null ? '' : raw).trim());
    }
    return map;
  }, [dictOptions]);

  const beds = useMemo(() => {
    const devices = babyMonitorDevices(yingshiDeviceList, selectedStoreId);
    return devices.map((device) => {
      const view = readBabyMonitorView(device, sse, nursingLabelByValue);
      const tone = toneOf(view);
      return {
        ...view,
        device,
        tone,
        sortNum: bedSortNum(view.bedNo, view.channelNo)
      };
    }).sort((a, b) => {
      const toneDiff = (TONE_ORDER[a.tone] ?? 9) - (TONE_ORDER[b.tone] ?? 9);
      if (toneDiff !== 0) return toneDiff;
      if (a.sortNum !== b.sortNum) return a.sortNum - b.sortNum;
      return String(a.bedNo).localeCompare(String(b.bedNo));
    });
  }, [yingshiDeviceList, selectedStoreId, sse, nursingLabelByValue]);

  const openBed = beds.find((bed) => bed.key === openKey) || null;

  const fetching = yingshiDeviceList?.fetching === true;
  const listReady = String(yingshiDeviceList?.query?.storeId ?? '') === selectedStoreId;

  const markWrong = useCallback(() => {
    if (!openBed || !openBed.device) return;
    if (!openBed.warning) {
      message.info('当前没有可标记的告警');
      return;
    }
    const device = openBed.device;
    if (device.isFalsePositive === true) {
      message.info('这路已经标记过错检');
      return;
    }
    const deviceId = String(device.deviceSerial || '').trim();
    const channel = device.channelNo == null ? '' : String(device.channelNo).trim();
    if (!deviceId || channel === '') {
      message.warning('缺少设备信息，暂时没法标记');
      return;
    }
    if (wrongPendingKey === openBed.key) return;
    setWrongPendingKey(openBed.key);
    markYingshiDeviceFalsePositive({ deviceId, channelNo: channel })
      .then(() => {
        dispatch(addMonitoringAlarmProcessed({ deviceId, channelNo: channel }));
        message.success('已标记为识别有误');
        if (!selectedStoreId) return null;
        return dispatch(fetchYingshiDeviceList({ query: { storeId: selectedStoreId } }));
      })
      .catch((error) => {
        message.error(apiErrorText(error, '标记失败'));
      })
      .finally(() => {
        setWrongPendingKey('');
      });
  }, [dispatch, openBed, selectedStoreId, wrongPendingKey]);

  if (!selectedStoreId) {
    return <div className="map-panel"><div className="vs-bed-empty">请选择门店</div></div>;
  }
  if (!listReady && fetching) {
    return <div className="map-panel"><div className="vs-bed-empty">加载中...</div></div>;
  }

  const wrongBusy = Boolean(openBed) && wrongPendingKey === openBed.key;

  return (
    <div className="map-panel">
      <div className="vs-bed-legend">
        {['normal', 'room', 'absent', 'alert', 'empty'].map((tone) => (
          <span key={tone} className="vs-bed-legend-item">
            <span className="vs-bed-legend-dot" style={{ background: BED_TONE[tone].color }} />
            {`${BED_TONE[tone].label} ${beds.filter((bed) => bed.tone === tone).length}`}
          </span>
        ))}
      </div>
      {beds.length === 0 ? (
        <div className="vs-bed-empty">本店暂无开启婴儿监控的摄像头</div>
      ) : (
        <div className="vs-bed-grid">
          {beds.map((bed) => {
            const cfg = BED_TONE[bed.tone];
            return (
              <button
                type="button"
                key={bed.key}
                className={`vs-bed-card vs-bed-card-${bed.tone}`}
                style={{ '--bed-color': cfg.color }}
                onClick={() => setOpenKey(bed.key)}
              >
                <span className="vs-bed-card-top">
                  <span className="vs-bed-status">{bed.statusTail}</span>
                </span>
                <span className="vs-bed-card-center">
                  <span className="vs-bed-label">床位</span>
                  <span className="vs-bed-num">{bed.bedNo || '--'}</span>
                </span>
                <span className="vs-bed-card-bottom">
                  <span className={bed.online ? 'vs-bed-online' : 'vs-bed-offline'}>
                    {bed.online ? '● 在线' : '● 离线'}
                  </span>
                  <span className="vs-bed-channel">{`通道 ${bed.channelNo ?? '--'}`}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      {openBed && createPortal(
        <div className="wa-overlay" onClick={() => setOpenKey('')}>
          <div className="wa-modal wa-modal-wide" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="wa-close" onClick={() => setOpenKey('')} title="关闭">×</button>
            <div className="wa-bed-list">
              <div className="wa-bed-list-title">婴儿床列表</div>
              <div className="wa-bed-list-scroll">
                {beds.map((bed) => {
                  const cfg = BED_TONE[bed.tone];
                  return (
                    <div
                      key={bed.key}
                      className={`wa-bed-item${bed.key === openKey ? ' selected' : ''} ${bed.tone}`}
                      style={{ '--c': cfg.color }}
                      onClick={() => setOpenKey(bed.key)}
                    >
                      <span className="wbi-num">{bed.bedNo || '--'}</span>
                      <span className="wbi-status" style={{ color: cfg.color }}>{bed.statusTail}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="wa-detail">
              <div className="wa-top">
                <span className="wa-tag wa-tag-dark">{BED_TONE[openBed.tone].label}</span>
                <span className="wa-tag wa-tag-yellow">{openBed.statusTail}</span>
                <span className="wa-title">{openBed.bedNo ? `${openBed.bedNo}号床` : (openBed.room || '婴儿床')}</span>
              </div>
              <div className="wa-time-row">
                <span className="wa-duration">位置 <em>{openBed.room || '--'}</em></span>
                <span className="wa-issue">{`通道 ${openBed.channelNo ?? '--'} · ${openBed.deviceSerial || '--'}`}</span>
              </div>
              <div
                className="wa-core"
                style={openBed.screenUrl ? {
                  background: `url('${openBed.screenUrl}') center center / cover no-repeat`
                } : undefined}
              >
                <div className="wa-video-mask"></div>
                <span className={`wa-view-flag ${openBed.screenUrl ? 'live' : 'old'}`}>
                  {openBed.screenUrl ? '● 实时画面' : '暂无画面'}
                </span>
                <div className="wa-old-imgs">
                  <div className="wa-old-imgs-title">历史记录</div>
                  <button type="button" className="wa-old-img-btn active">
                    <span className="wa-old-img-time">实时</span>
                    <span className="wa-old-img-tag">当前</span>
                  </button>
                  <div className="wa-old-empty">暂无历史</div>
                </div>
              </div>
              <div className="wa-bottom">
                <button
                  type="button"
                  className="wa-miss-btn"
                  disabled={wrongBusy}
                  onClick={markWrong}
                >
                  <span className="wa-miss-icon">⊙</span>
                  <span>{wrongBusy ? '提交中' : '错检'}</span>
                </button>
                <button type="button" className="wa-takeover-btn">
                  <span className="wa-takeover-icon">🛡</span>
                  <span>接管</span>
                </button>
                <div className="wa-bottom-info">
                  <span className={openBed.online ? 'wa-online' : 'wa-online is-off'}>
                    {openBed.online ? '在线' : '离线'}
                  </span>
                  <span className="wa-bed-no">{openBed.bedNo ? `${openBed.bedNo}号床` : '--'}</span>
                  <span className="wa-channel">通道 <em>{openBed.channelNo ?? '--'}</em></span>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default MapView;
